/**
 * keyInit.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Called once after a successful login.
 *
 * Possible outcomes:
 *   'existing'     — key already on this device, nothing to do
 *   'new_key'      — fresh key generated & uploaded; show passphrase setup UI
 *   'backup_found' — server backup exists; show passphrase restore UI
 * ─────────────────────────────────────────────────────────────────────────────
 */

import axios from 'axios'
import { baseUrl, config } from 'features/constants'
import { generateKeyPair } from './cryptoService'
import { hasPrivateKey, savePrivateKey, savePublicKey } from './keyStore'

/**
 * Initialise E2EE keys after login.
 * Returns { status, backup? }
 */
export async function initE2EEKeys(token, userId) {
  // Step 1: key already on this device for this user — nothing to do
  if (hasPrivateKey(userId)) return { status: 'existing' }

  // Step 2: check for a server-side backup (user set one up on another device)
  try {
    const { data } = await axios.get(
      `${baseUrl}/api/users/getE2EEBackup`,
      config(token)
    )
    if (data?.e2eeBackup?.encryptedKey) {
      return { status: 'backup_found', backup: data.e2eeBackup }
    }
  } catch {
    // Network error / 404 — treat as no backup
  }

  // Step 3: generate a fresh key pair
  const { publicKey, privateKey } = generateKeyPair()
  savePrivateKey(userId, privateKey)
  savePublicKey(userId, publicKey)

  // Upload public key to server
  try {
    await axios.post(
      `${baseUrl}/api/users/uploadPublicKey`,
      { publicKey },
      config(token)
    )
  } catch (err) {
    console.warn('[E2EE] Failed to upload public key:', err)
  }

  return { status: 'new_key' }
}

/**
 * Fetch another user's public key from the server.
 * Returns null if they haven't set up E2EE yet.
 */
export async function fetchPublicKey(userId, token) {
  try {
    const { data } = await axios.get(
      `${baseUrl}/api/users/${userId}/publicKey`,
      config(token)
    )
    return data?.publicKey ?? null
  } catch {
    return null
  }
}
